import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  PageHeader,
  StatCard,
  Card,
  SectionHeader,
  Badge,
  Spinner,
  QueryError,
} from "../components";
import { apiGet } from "../lib/api";

interface ContextPack {
  id: string;
  name: string;
  description: string;
  config: { contextPack?: string; thinkingLevel?: string; modelTier?: string };
  isDefault: boolean;
}

export default function ContextPacks() {
  const { data, isLoading, error } = useQuery<{ packs: ContextPack[] }>({
    queryKey: ["context-packs"],
    queryFn: () => apiGet<{ packs: ContextPack[] }>("/api/context-packs"),
  });

  const packs = data?.packs ?? [];
  const defaultPack = packs.find((pack) => pack.isDefault)?.name ?? "None";

  if (isLoading && !data) {
    return (
      <div>
        <PageHeader
          title="Context Packs"
          subtitle="Stocktake surface — hidden from the main operator navigation"
        />
        <div className="flex items-center justify-center py-20">
          <Spinner size={24} />
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div>
        <PageHeader
          title="Context Packs"
          subtitle="Stocktake surface — hidden from the main operator navigation"
        />
        <QueryError error={error} queryKeys={[["context-packs"]]} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Context Packs"
        subtitle="Hidden from the main operator UI until the concept is tied to a clear operator workflow"
      />

      <Card className="mb-8">
        <SectionHeader
          title="Why this is hidden"
          subtitle="The concept may still be useful, but the operator-facing purpose is not clear enough yet"
        />
        <div className="space-y-2 text-sm" style={{ color: "var(--c-text-secondary)" }}>
          <p>
            Context packs stay in backend storage for now, but they are removed from the primary
            operator navigation until we confirm what real operator task they support.
          </p>
          <p>
            If this becomes a real operator control again, it needs a clearer contract with tenant
            configuration and model/runtime policy.
          </p>
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <StatCard title="Stored Packs" value={String(packs.length)} />
        <StatCard title="Default Pack" value={defaultPack} />
        <StatCard title="Operator Status" value="Stocktake" color="var(--c-warning)" />
      </div>

      <Card className="mb-8">
        <SectionHeader
          title="Current inventory"
          subtitle="Read-only until product intent is confirmed"
        />
        {packs.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--c-text-secondary)" }}>
            No context packs are stored.
          </p>
        ) : (
          <div className="space-y-4">
            {packs.map((pack) => (
              <div
                key={pack.id}
                className="rounded-lg p-4"
                style={{ border: "1px solid var(--c-border)", background: "var(--c-surface-2)" }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-medium" style={{ color: "var(--c-text)" }}>
                    {pack.name}
                  </span>
                  {pack.isDefault && <Badge color="green">default</Badge>}
                </div>
                <p className="text-sm mb-3" style={{ color: "var(--c-text-secondary)" }}>
                  {pack.description || "No description"}
                </p>
                <div className="flex flex-wrap gap-2 text-xs">
                  {pack.config.contextPack && <Badge color="blue">{pack.config.contextPack}</Badge>}
                  {pack.config.thinkingLevel && (
                    <Badge color="amber">{pack.config.thinkingLevel}</Badge>
                  )}
                  {pack.config.modelTier && <Badge color="gray">{pack.config.modelTier}</Badge>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <SectionHeader
          title="Current stocktake"
          subtitle="Questions to answer before this returns to the operator UI"
        />
        <ul
          className="space-y-2 text-sm"
          style={{ color: "var(--c-text-secondary)", paddingLeft: 18 }}
        >
          <li>
            What operator task is this solving that tenant configuration does not already cover?
          </li>
          <li>Should packs control model/runtime policy, optimizer hints, or both?</li>
          <li>
            Is this a real operator feature or an internal optimization concept that should stay
            backend-only?
          </li>
        </ul>
        <p className="text-sm mt-4" style={{ color: "var(--c-text-secondary)" }}>
          Version catalog and deployment state remain available on the{" "}
          <Link to="/versions" style={{ color: "var(--c-primary)" }}>
            Versions
          </Link>{" "}
          and{" "}
          <Link to="/tenants" style={{ color: "var(--c-primary)" }}>
            Tenants
          </Link>{" "}
          pages.
        </p>
      </Card>
    </div>
  );
}
